import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { TFunction } from "i18next";
import { IChannel } from "@Interfaces";
import { NotificationActions } from "@Store";
import {
    CHANNEL_MILESTONE_TARGET,
    loadCompletedChannels,
    persistCompletedChannels,
    resolveAuthorId,
    resolveAuthorName,
} from "./channelCompletion.utils";
import { DOUBLE_VOTE_CLEAR_MS } from "./doubleVoteConstants";

type UseChannelCompletionProgressParams = {
    channel: IChannel | null | undefined;
    hasCompletedVoting: boolean;
    translate: TFunction;
};

// Allow the standard success toast to auto-hide before the reward banner appears.
const REWARD_SNACKBAR_DELAY_MS = 2200;

/**
 * Tracks channel completion progress for the current author, keeps the per-author
 * list persisted in localStorage, and coordinates the completion/reward snackbars.
 */
export const useChannelCompletionProgress = ({
    channel,
    hasCompletedVoting,
    translate,
}: UseChannelCompletionProgressParams) => {
    const dispatch = useDispatch<any>();
    const isMountedRef = useRef(true);
    const rewardNotifiedRef = useRef(false);
    const rewardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const doubleVoteCleanupTimerRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const completionChangeRef = useRef<{
        type: "added" | "removed";
        count: number;
    } | null>(null);
    const [completedChannelIds, setCompletedChannelIds] = useState<string[]>(
        []
    );
    const authorId = useMemo(() => resolveAuthorId(channel), [channel]);

    // Reload persisted progress whenever the resolved author changes.
    useEffect(() => {
        isMountedRef.current = true;
        const storedChannels = loadCompletedChannels(authorId);

        setCompletedChannelIds(storedChannels);
        if (rewardTimerRef.current) {
            clearTimeout(rewardTimerRef.current);
            rewardTimerRef.current = null;
        }
        rewardNotifiedRef.current = false;
    }, [authorId]);

    // Sync the in-memory list with the current channel's completion status.
    useEffect(() => {
        const channelId = channel?._id;

        if (!channelId || !authorId) {
            return;
        }

        setCompletedChannelIds((previous) => {
            const alreadyCompleted = previous.includes(channelId);

            if (hasCompletedVoting) {
                if (alreadyCompleted) {
                    return previous;
                }

                const updated = [...previous, channelId];
                persistCompletedChannels(authorId, updated);
                completionChangeRef.current = {
                    type: "added",
                    count: updated.length,
                };
                return updated;
            }

            if (!alreadyCompleted) {
                return previous;
            }

            const updated = previous.filter((id) => id !== channelId);
            persistCompletedChannels(authorId, updated);
            completionChangeRef.current = {
                type: "removed",
                count: updated.length,
            };
            return updated;
        });
    }, [authorId, channel, hasCompletedVoting]);

    // Show the per-channel completion success snackbar whenever we add a new id.
    useEffect(() => {
        const completionChange = completionChangeRef.current;

        if (!completionChange || completionChange.type !== "added") {
            completionChangeRef.current = null;
            return;
        }

        dispatch(
            NotificationActions.notification({
                snackbarType: "success",
                snackbarMessage: translate("invitation.all_polls_completed"),
            })
        );

        // Only for double vote channels: after the cooldown, remove the channel from completed channels in localStorage
        const channelId = channel?._id;
        if (channelId && authorId && channel?.settings?.allow_double_vote) {
            const timeoutMs = DOUBLE_VOTE_CLEAR_MS;

            if (doubleVoteCleanupTimerRef.current) {
                clearTimeout(doubleVoteCleanupTimerRef.current);
            }

            doubleVoteCleanupTimerRef.current = setTimeout(() => {
                // Always persist the cleanup, even if component unmounted.
                setCompletedChannelIds((prev) => {
                    if (!prev.includes(channelId)) return prev;
                    const updated = prev.filter((id) => id !== channelId);
                    persistCompletedChannels(authorId, updated);
                    return isMountedRef.current ? updated : prev;
                });
                doubleVoteCleanupTimerRef.current = null;
            }, timeoutMs);
        }

        completionChangeRef.current = null;
    }, [completedChannelIds, dispatch, translate, channel, authorId]);

    // Defer the reward snackbar until the standard success toast has had time to display.
    useEffect(() => {
        const clearRewardTimer = () => {
            if (rewardTimerRef.current) {
                clearTimeout(rewardTimerRef.current);
                rewardTimerRef.current = null;
            }
        };

        if (!channel) {
            clearRewardTimer();
            return;
        }

        if (completedChannelIds.length >= CHANNEL_MILESTONE_TARGET) {
            if (rewardNotifiedRef.current || rewardTimerRef.current) {
                return;
            }

            const channelTitle = channel.title ?? null;
            const authorName =
                resolveAuthorName(channel, channelTitle) ??
                translate("invitation.default_author_name");

            rewardTimerRef.current = setTimeout(() => {
                dispatch(
                    NotificationActions.notification({
                        snackbarType: "reward",
                        snackbarMessage: translate(
                            "invitation.five_channels_completed",
                            {
                                authorName,
                            }
                        ),
                    })
                );
                rewardNotifiedRef.current = true;
                rewardTimerRef.current = null;
            }, REWARD_SNACKBAR_DELAY_MS);
            return;
        }

        if (rewardNotifiedRef.current || rewardTimerRef.current) {
            clearRewardTimer();
            rewardNotifiedRef.current = false;
            dispatch(NotificationActions.resetNotification());
        }
    }, [channel, completedChannelIds, dispatch, translate]);

    // Clean up scheduled timers when the hook unmounts.
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (rewardTimerRef.current) {
                clearTimeout(rewardTimerRef.current);
            }
            // Keep doubleVoteCleanupTimerRef running to ensure localStorage cleanup even if unmounted.
        };
    }, []);

    return {
        authorId,
        completedChannelCount: completedChannelIds.length,
    };
};
